import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiErr, downloadFile } from "@/lib/api";
import { Loader, Card, PageHeader } from "@/components/ui-helpers";
import { SheetSyncBar } from "@/components/GoogleSheetsCard";
import { ShortTermColumnsDialog } from "@/components/ShortTermColumnsDialog";
import { CoursePlanDialog } from "@/components/CoursePlanDialog";
import { CourseTabsDialog } from "@/components/CourseTabsDialog";
import { CourseImportDialog } from "@/components/CourseImportDialog";
import { PlanStatus } from "@/components/PaymentPlan";
import { toast } from "sonner";
import { Plus, Trash2, Download, Columns3, Search, Upload, FolderCog, CalendarClock } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ShortTermCourses() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "";
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [college, setCollege] = useState("");
  const [colsOpen, setColsOpen] = useState(false);
  const [tabsOpen, setTabsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [planRow, setPlanRow] = useState(null);
  const qs = () => [tab && `tab=${encodeURIComponent(tab)}`, q && `q=${encodeURIComponent(q)}`, status && `status=${encodeURIComponent(status)}`, college && `college=${encodeURIComponent(college)}`].filter(Boolean).join("&");
  const load = () => api.get(`/short-term?${qs()}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, status, college, tab]); // eslint-disable-line
  useEffect(() => {
    const sid = params.get("student");
    if (sid) api.get(`/short-term/rows/${sid}`).then((r) => setPlanRow(r.data)).catch(() => {});
  }, []); // eslint-disable-line
  const switchTab = (key) => { setCollege(""); setParams({ tab: key }); };

  const addRow = async () => {
    try {
      await api.post("/short-term/rows", { data: {}, tab: data.tab });
      if (q || status || college) { setQ(""); setStatus(""); setCollege(""); toast.success("Student row added — filters cleared so you can see it"); } else load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveCell = async (row, key, value) => {
    if (String(row.data[key] ?? "") === String(value ?? "")) return;
    try {
      const { data: upd } = await api.patch(`/short-term/rows/${row.id}`, { data: { [key]: value } });
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.id === row.id ? upd : r)) }));
      api.get(`/short-term?${qs()}`).then((r) => setData((d) => ({ ...d, totals: r.data.totals, filtered_totals: r.data.filtered_totals, colleges: r.data.colleges })));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const removeRow = async (row) => {
    if (!window.confirm(`Delete ${row.data.student_name || "this row"}?`)) return;
    try { await api.delete(`/short-term/rows/${row.id}`); toast.success("Row deleted"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const exportAs = (fmt) => downloadFile(`/short-term/export?format=${fmt}&${qs()}`, `${(data.tabs.find((x) => x.key === data.tab)?.label || "courses").replace(/[^\w-]+/g, "-")}.${fmt}`).catch((e) => toast.error(apiErr(e)));

  if (!data) return <Loader />;
  const t = data.totals;
  const tabLabel = data.tabs.find((x) => x.key === data.tab)?.label || "Courses";
  const btn = "inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl font-semibold text-sm";
  return (
    <div className="space-y-5">
      <PageHeader title="Courses" subtitle="College course students, with their own billing and instalments — kept separate from case billing.">
        <div className="flex flex-wrap gap-2">
          <button data-testid="st-columns-btn" onClick={() => setColsOpen(true)} className={btn}><Columns3 className="h-4 w-4" /> Columns</button>
          <button data-testid="st-import-btn" onClick={() => setImportOpen(true)} className={btn}><Upload className="h-4 w-4" /> Import Excel</button>
          <button data-testid="st-export-xlsx" onClick={() => exportAs("xlsx")} className={btn}><Download className="h-4 w-4" /> Excel</button>
          <button data-testid="st-export-csv" onClick={() => exportAs("csv")} className={btn}><Download className="h-4 w-4" /> CSV</button>
          <button data-testid="st-add-row" onClick={addRow} className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm"><Plus className="h-4 w-4" /> Add student</button>
        </div>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200" data-testid="st-tabs">
        {data.tabs.map((x) => (
          <button key={x.key} data-testid={`st-tab-${x.key}`} onClick={() => switchTab(x.key)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${x.key === data.tab ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {x.label} <span className="ml-1 text-[11px] font-normal text-slate-400">{x.count}</span>
          </button>
        ))}
        <button data-testid="st-manage-tabs" onClick={() => setTabsOpen(true)} className="ml-1 px-3 py-2 text-xs font-semibold text-slate-500 hover:text-primary inline-flex items-center gap-1"><FolderCog className="h-4 w-4" /> Manage tabs</button>
      </div>

      <SheetSyncBar onSynced={load} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="st-totals">
        {[["fees", "Total fees", t.fees, "text-slate-900"], ["collected", "Collected", t.collected, "text-emerald-600"], ["outstanding", "Outstanding", t.outstanding, "text-rose-600"]].map(([k, l, v, c]) => (
          <Card key={k} className="p-4"><div className="text-xs text-slate-500">{l}</div><div data-testid={`st-total-${k}`} className={`font-head font-extrabold text-2xl ${c}`}>{money(v)}</div></Card>
        ))}
        <Card className="p-4"><div className="text-xs text-slate-500">Students</div><div data-testid="st-total-students" className="font-head font-extrabold text-2xl text-slate-900">{t.students}</div></Card>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="st-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search students, courses, notes…" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm" />
        </div>
        <select data-testid="st-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm"><option value="">All statuses</option>{data.statuses.map((s) => <option key={s}>{s}</option>)}</select>
        <select data-testid="st-filter-college" value={college} onChange={(e) => setCollege(e.target.value)} className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm"><option value="">All colleges</option>{data.colleges.map((s) => <option key={s}>{s}</option>)}</select>
      </div>

      <ShortTermGrid data={data} tabLabel={tabLabel} onSave={saveCell} onRemove={removeRow} onPlan={setPlanRow} />
      {(q || status || college) && <div data-testid="st-filtered-summary" className="text-xs text-slate-500">Showing {data.rows.length} · fees {money(data.filtered_totals.fees)} · collected {money(data.filtered_totals.collected)} · outstanding {money(data.filtered_totals.outstanding)}</div>}

      {colsOpen && <ShortTermColumnsDialog columns={data.columns} onClose={() => setColsOpen(false)} onSaved={load} />}
      {tabsOpen && <CourseTabsDialog tabs={data.tabs} onClose={() => setTabsOpen(false)} onSaved={load} />}
      {importOpen && <CourseImportDialog tabs={data.tabs} tab={data.tab} onClose={() => setImportOpen(false)} onDone={(k) => (k === data.tab ? load() : switchTab(k))} />}
      {planRow && <CoursePlanDialog row={planRow} tabs={data.tabs} frequencies={data.frequencies} onClose={() => { setPlanRow(null); if (params.get("student")) setParams({ tab: data.tab }); }} onChanged={load} />}
    </div>
  );
}

function PlanCell({ row, onPlan }) {
  const p = row.plan;
  const active = p?.status === "active";
  return (
    <button data-testid={`st-plan-btn-${row.id}`} onClick={() => onPlan(row)} className="w-full px-2 py-1.5 text-left text-xs inline-flex items-center gap-1.5 hover:text-primary">
      <CalendarClock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      {active ? <><span className="text-slate-700">{p.installments.filter((i) => i.status === "paid").length}/{p.count} paid</span>{p.next && <PlanStatus s={p.next.status} />}</>
        : p?.status === "completed" ? <span className="text-emerald-600 font-semibold">Paid off</span> : <span className="text-slate-400">Set up</span>}
    </button>
  );
}

function ShortTermGrid({ data, tabLabel, onSave, onRemove, onPlan }) {
  const cols = data.columns;
  return (
    <Card className="overflow-auto max-h-[65vh]" data-testid="st-grid">
      <table className="w-full text-sm border-collapse">
        <thead className="sticky top-0 z-10 bg-slate-100 text-slate-600 text-xs">
          <tr>
            <th className="w-10 min-w-[2.5rem] sticky left-0 z-20 bg-slate-100 px-2 py-2 border-b border-r border-slate-200 text-center font-semibold">#</th>
            {cols.map((c, ci) => <th key={c.key} data-testid={`st-col-${c.key}`} className={`px-2 py-2 border-b border-r border-slate-200 text-left font-semibold whitespace-nowrap min-w-[130px] ${ci === 0 ? "sticky left-10 z-20 bg-slate-100" : ""}`}>{c.label}</th>)}
            <th className="px-2 py-2 border-b border-r border-slate-200 text-left font-semibold whitespace-nowrap min-w-[140px]">Instalments</th>
            <th className="w-10 border-b border-slate-200" />
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && <tr><td colSpan={cols.length + 3} className="px-4 py-8 text-center text-slate-400" data-testid="st-empty">No students in {tabLabel} yet — click “Add student” or “Import Excel”.</td></tr>}
          {data.rows.map((r, i) => (
            <tr key={r.id} data-testid={`st-row-${r.id}`} className="hover:bg-sky-50/40">
              <td className="w-10 min-w-[2.5rem] sticky left-0 z-[5] px-2 py-1 border-b border-r border-slate-100 text-center text-xs text-slate-400 bg-slate-50">{i + 1}</td>
              {cols.map((c, ci) => <td key={c.key} className={`border-b border-r border-slate-100 p-0 ${ci === 0 ? "sticky left-10 z-[5] bg-white" : ""}`}><Cell row={r} col={c} statuses={data.statuses} onSave={onSave} /></td>)}
              <td className="border-b border-r border-slate-100 p-0"><PlanCell row={r} onPlan={onPlan} /></td>
              <td className="border-b border-slate-100 text-center"><button data-testid={`st-delete-${r.id}`} onClick={() => onRemove(r)} className="p-1.5 text-slate-300 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Cell({ row, col, statuses, onSave }) {
  const v = row.data[col.key] ?? "";
  const [val, setVal] = useState(v);
  useEffect(() => { setVal(v); }, [v]);
  const tid = `st-cell-${row.id}-${col.key}`;
  const base = "w-full px-2 py-1.5 bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white";
  if (col.type === "computed") return <div data-testid={tid} className={`px-2 py-1.5 font-semibold ${Number(v) > 0 ? "text-rose-600" : "text-emerald-600"}`}>{money(v)}</div>;
  if (col.type === "status") return (
    <select data-testid={tid} value={val} onChange={(e) => { setVal(e.target.value); onSave(row, col.key, e.target.value); }} className={base}>
      <option value="" />{statuses.map((s) => <option key={s}>{s}</option>)}
    </select>
  );
  return (
    <input data-testid={tid} type={col.type === "date" ? "date" : "text"} inputMode={col.type === "number" ? "decimal" : undefined}
      value={val} onChange={(e) => setVal(e.target.value)} onBlur={() => onSave(row, col.key, val)}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} className={`${base} ${col.type === "number" ? "text-right" : ""}`} />
  );
}
